import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { Verdict, AssessmentStatus, Dimension } from '@/types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function verdictColor(verdict?: Verdict | null) {
  switch (verdict) {
    case 'VERDE': return 'text-verde-dark bg-verde-light border-verde'
    case 'GIALLO': return 'text-giallo-dark bg-giallo-light border-giallo'
    case 'ROSSO': return 'text-rosso-dark bg-rosso-light border-rosso'
    case 'VIETATO': return 'text-white bg-rosso-dark border-rosso-dark'
    default: return 'text-gray-600 bg-gray-100 border-gray-300'
  }
}

export function verdictBadgeColor(verdict?: Verdict | null) {
  switch (verdict) {
    case 'VERDE': return 'bg-verde text-white'
    case 'GIALLO': return 'bg-giallo-dark text-white'
    case 'ROSSO': return 'bg-rosso text-white'
    case 'VIETATO': return 'bg-rosso-dark text-white'
    default: return 'bg-gray-400 text-white'
  }
}

export function verdictLabel(verdict?: Verdict | null, lang = 'it'): string {
  const labels: Record<string, Record<string, string>> = {
    it: {
      VERDE: 'Approvata',
      GIALLO: 'Sperimentazione',
      ROSSO: 'Non raccomandata',
      VIETATO: 'Vietata (Art. 5)',
    },
    en: {
      VERDE: 'Approved',
      GIALLO: 'Limited use',
      ROSSO: 'Not recommended',
      VIETATO: 'Prohibited (Art. 5)',
    },
  }
  return verdict ? labels[lang]?.[verdict] ?? verdict : '–'
}

export function statusLabel(status: AssessmentStatus, lang = 'it'): string {
  const labels: Record<string, Record<string, string>> = {
    it: {
      DRAFT: 'Bozza',
      SEARCHING: 'Ricerca web…',
      ANALYZING: 'Analisi IA…',
      COMPLETED: 'Completata',
      FAILED: 'Errore',
    },
    en: {
      DRAFT: 'Draft',
      SEARCHING: 'Searching…',
      ANALYZING: 'Analyzing…',
      COMPLETED: 'Completed',
      FAILED: 'Failed',
    },
  }
  return labels[lang]?.[status] ?? status
}

export function dimensionLabel(dim: Dimension): string {
  const labels: Record<Dimension, string> = {
    ETICA: 'Etica e Bias',
    TECNICA: 'Sicurezza Tecnica',
    NORMATIVA_AI_ACT: 'AI Act EU',
    NORMATIVA_GDPR_MIM: 'GDPR & MIM',
    SUPERVISIONE_UMANA: 'Supervisione Umana',
  }
  return labels[dim] ?? dim
}

export function scoreColor(score: number): string {
  if (score >= 4) return 'text-verde-dark'
  if (score >= 2.5) return 'text-giallo-dark'
  return 'text-rosso-dark'
}

export function scoreBarColor(score: number): string {
  if (score >= 4) return 'bg-verde'
  if (score >= 2.5) return 'bg-giallo'
  return 'bg-rosso'
}

export function formatDate(dateStr: string, lang = 'it'): string {
  return new Date(dateStr).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function appCategoryLabel(cat: string, lang = 'it'): string {
  const labels: Record<string, Record<string, string>> = {
    it: {
      TUTORING_IA: 'Tutoring IA',
      GENERAZIONE_CONTENUTI: 'Generazione Contenuti',
      VALUTAZIONE_AUTOMATICA: 'Valutazione Automatica',
      ROBOTICA_LAB: 'Robotica / Lab',
      AMMINISTRAZIONE: 'Amministrazione',
      ALTRO: 'Altro',
    },
    en: {
      TUTORING_IA: 'AI Tutoring',
      GENERAZIONE_CONTENUTI: 'Content Generation',
      VALUTAZIONE_AUTOMATICA: 'Automated Assessment',
      ROBOTICA_LAB: 'Robotics / Lab',
      AMMINISTRAZIONE: 'Administration',
      ALTRO: 'Other',
    },
  }
  return labels[lang]?.[cat] ?? labels.it[cat] ?? cat
}

export function contextLabel(ctx: string): string {
  const labels: Record<string, string> = {
    SEGRETERIA: 'Segreteria',
    LABORATORIO: 'Laboratorio',
    CLASSE: 'Classe',
  }
  return labels[ctx] ?? ctx
}

export function userTypeLabel(ut: string): string {
  const labels: Record<string, string> = {
    GOVERNANCE: 'Governance',
    SEGRETERIA: 'Segreteria',
    DOCENTI: 'Docenti',
    ALUNNI: 'Alunni',
  }
  return labels[ut] ?? ut
}

export function roleLabel(role: string): string {
  const labels: Record<string, string> = {
    PLATFORM_ADMIN: 'Amministratore Piattaforma',
    DIRIGENTE: 'Dirigente Scolastico',
    DPO: 'DPO',
    DOCENTE: 'Docente / Animatore digitale',
    // Legacy value (role removed): shown identically to a docente.
    ANIMATORE_DIGITALE: 'Docente / Animatore digitale',
    SEGRETERIA: 'Segreteria',
    // Legacy value (role removed): students only ever act through a teacher.
    ALUNNO: 'Alunno',
    ISPETTORE: 'Ispettore (sola consultazione)',
    // Legacy value (renamed to ISPETTORE): kept so historical audit log
    // entries logged under the old name still render a friendly label.
    LETTORE: 'Ispettore (sola consultazione)',
  }
  return labels[role] ?? role
}
